import { CommissionType } from '__generated__/athena';

import { COSMOS_URL } from '../../globals';
import { FALLBACK_URL } from '../components/CoverImages';

export const getMonthlyHoaFee = (
  hoaFeeAmount?: number | null,
  hoaFeeFrequency?: string | null,
): number | undefined => {
  if (!hoaFeeAmount || !hoaFeeFrequency) {
    return;
  }
  if (hoaFeeFrequency === 'monthly') {
    return hoaFeeAmount;
  }
  if (hoaFeeFrequency === 'annually') {
    return Math.round(hoaFeeAmount / 12);
  }
  return;
};

export const calculateTotalBathrooms = (
  bathroomsFull?: number | null,
  bathroomsHalf?: number | null,
) => {
  let totalBathrooms = 0;
  if (bathroomsFull) {
    totalBathrooms += bathroomsFull;
  }
  if (bathroomsHalf) {
    totalBathrooms += Math.round(bathroomsHalf * 0.5);
  }
  return totalBathrooms;
};

type PropertyAddress = {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  unitNumber?: string | null;
};

export function getHumanReadableAddress(
  street?: string | null,
  city?: string | null,
  state?: string | null,
  postalCode?: string | null,
) {
  if (!street || !city || !state || !postalCode) {
    return '';
  }
  return `${street}, ${city}, ${state} ${postalCode}`;
}

export const getFormattedAddresses = (props: PropertyAddress): { short: string; full: string } => {
  return {
    short: [props.street, props.unitNumber && `#${props.unitNumber}`].filter(Boolean).join(' '),
    full: [
      props.street,
      props.unitNumber && `#${props.unitNumber}`,
      (props.city || props.state) && [props.city, props.state].filter(Boolean).join(', '),
      props.postalCode,
    ]
      .filter(Boolean)
      .join(' '),
  };
};

export const getReadableDwellingType = (dwellingType: string | null | undefined) => {
  if (!dwellingType) {
    return 'Unknown';
  }
  return dwellingType
    ?.split('_')
    .map((input) => input.charAt(0).toUpperCase() + input.slice(1).toLowerCase())
    .join(' ');
};

export const getReadableListingStatus = (listingState: string | null | undefined) => {
  if (!listingState) {
    return 'Unknown';
  }
  if (listingState === 'SELL_REQUEST_TEMP_OFF_MARKET') {
    return 'Off Market';
  }
  return listingState
    .substring('SELL_REQUEST_'.length)
    .split('_')
    .map((input) => input.charAt(0).toUpperCase() + input.slice(1).toLowerCase())
    .join(' ');
};

export const getReadableHoaFeatures = (monthlyHoaFee?: number | null, features?: Array<string>) => {
  if (!monthlyHoaFee || !features || features?.length === 0) {
    return undefined;
  }
  return `$${monthlyHoaFee} includes: ${features.map(
    (item) => `${item?.replace(/([A-Z])/g, ' $1').toLowerCase()}`,
  )}`;
};

export const getSeoFields = (
  addressId: string | undefined,
  displaySlug: string | null | undefined,
  status: string | null | undefined,
  street: string | undefined | null,
  unitNumber: string | undefined | null,
  city: string | undefined | null,
  state: string | undefined | null,
  postalCode: string | undefined | null,
  sqFtLot: number | null | undefined,
  bedrooms: number | null | undefined,
  bathrooms: number | null | undefined,
  yearBuilt: string | number | null | undefined,
  coverImageProp: string | null | undefined,
) => {
  const readableAddress = getHumanReadableAddress(street, city, state, postalCode);
  const title = `Home for sale - ${readableAddress} | Opendoor`;
  const canonicalUrl = displaySlug
    ? `${COSMOS_URL}/properties/${displaySlug}/aid_${addressId}`
    : `${COSMOS_URL}/properties/${street ? `${street?.toLowerCase()?.replaceAll(' ', '-')}-` : ''}${
        unitNumber ? `${unitNumber}-` : ''
      }${
        city ? `${city?.toLowerCase()}-` : ''
      }-${state?.toLowerCase()}${postalCode}/aid_${addressId}`;
  const forSale = getReadableListingStatus(status) === 'Active';
  const metaDescription = `${readableAddress} is currently ${
    forSale ? 'for sale' : 'not for sale'
  }. The ${sqFtLot ? `${sqFtLot} sq ft.` : ''} home ${
    yearBuilt ? `was built in ${yearBuilt}` : ''
  } ${bedrooms ? `and has ${bedrooms} bedrooms` : ''} ${
    bathrooms ? `and has ${bathrooms} bathrooms` : ''
  }. Learn more about the home on Opendoor.`;
  let coverImage = FALLBACK_URL;
  if (coverImageProp) {
    coverImage = `${coverImageProp}?${new URLSearchParams({
      preset: '3:2-sm',
      service: 'cosmos',
    }).toString()}`;
  }
  return { readableAddress, canonicalUrl, metaDescription, title, coverImage };
};

export const getBuyerAgentCommissionAmountFormatted = (
  type: CommissionType | null | undefined,
  value: number | null | undefined,
): string => {
  if (!value || !type) {
    return '';
  }
  return type === 'RATE_BPS' ? `${value / 100}%` : `$${value / 100}`;
};

export const buyerAgentCommissionModalHeader = `What’s this mean?`;

export const buyerAgentCommissionModalBody = `The seller pays a fee to their agent when the home sells. And they’ve agreed to share part of that commission with the buyer’s agent or their brokerage. To get that commission, they have to be a member of the MLS.`;
